import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import CloseButton from "react-bootstrap/CloseButton";
import Button from "react-bootstrap/Button";
import { styled } from "@mui/system";
import { ModalSuccessLogo, ModalBottomLogo } from "../assets/icons";
import {
  DISPLAY_BLOCK,
  DISPLAY_FLEX,
  FLEX_ALIGN_CENTER,
  FLEX_DIRECTION_ROW,
  FLEX_JUSTIFY_CENTER,
  MAX_WIDTH,
  TEXT_ALIGN_CENTER,
} from "../config/styles";
import { Typography } from "@mui/material";
import { colors } from "../config/colors";

const CopyLinkModalStyled = styled(Modal)(({ theme }) => ({
  width: "408px",
  margin: "0 auto",
  "& .custom-modal": {
    width: "408px !important",
    margin: "0 auto !important",
  },
  "& .modal-header": {
    borderBottom: "none",
    backgroundColor: theme.palette.primary.main,
    height: "159px",
    alignItems: "start",
    display: DISPLAY_BLOCK,
  },
  "& .modal-header-wrapper": {
    display: DISPLAY_FLEX,
    flexDirection: FLEX_DIRECTION_ROW,
    justifyContent: FLEX_JUSTIFY_CENTER,
    alignItems: FLEX_ALIGN_CENTER,
    width: MAX_WIDTH,
    paddingTop: "8px",
  },
  "& .modal-body": {
    padding: "20px",
    textAlign: "center",
    fontSize: "16px",
    color: theme.palette.text.primary,
  },
  "& .button-style": {
    width: "170px",
    padding: "8px 16px",
    gap: "8px",
    height: "36px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "bold",
    border: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  "& .copy-button": {
    width: "170px",
    padding: "8px 16px",
    gap: "8px",
    height: "36px",
    backgroundColor: "#6c757d",
    color: "#fff",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "bold",
    border: "none",
    "&:hover": {
      backgroundColor: "#5a6268",
    },
  },
  "& .link-container": {
    backgroundColor: "#f5f5f5",
    padding: "12px",
    borderRadius: "8px",
    marginTop: "16px",
    wordBreak: "break-all",
  },
}));

export const CopyLinkModal = ({ show, onHide, title, link }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    if (link) {
      // Create a temporary textarea element for copying
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleClose = () => {
    setIsCopied(false);
    onHide();
  };

  return (
    <CopyLinkModalStyled
      show={show}
      onHide={handleClose}
      centered
      contentClassName="custom-modal"
    >
      <Modal.Header bsPrefix="modal-header">
        <div className="d-flex justify-content-end w-100">
          <CloseButton variant="white" onClick={handleClose} />
        </div>
        <div className="modal-header-wrapper">
          <ModalSuccessLogo />
        </div>
        <div style={{ marginTop: "48px", textAlign: TEXT_ALIGN_CENTER }}>
          <ModalBottomLogo />
        </div>
      </Modal.Header>

      <Modal.Body className="py-4">
        <Typography variant="body2" className="w-100">
          {title}
        </Typography>
        {link && (
          <div className="link-container">
            <Typography
              variant="subtitle2"
              className="w-100"
              component="p"
              sx={{ color: colors.grayDark, fontSize: "12px" }}
            >
              {link}
            </Typography>
          </div>
        )}
      </Modal.Body>

      <div className="d-flex gap-2 justify-content-center mb-4">
        <Button className="copy-button" onClick={handleCopy}>
          {isCopied ? "Copied!" : "Copy Link"}
        </Button>
        <Button className="button-style" onClick={handleClose}>
          Done
        </Button>
      </div>
    </CopyLinkModalStyled>
  );
};

CopyLinkModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
};
